import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Form } from 'react-bootstrap'

export const parseEntries = value =>
  (value || '')
    .split(',')
    .map(entry => entry.trim())
    .filter(Boolean)

// Add the entries in `draft` (itself possibly comma-separated) to the
// comma-joined `value`, skipping duplicates case-insensitively.
export const addEntries = (value, draft) => {
  const entries = parseEntries(value)
  const seen = new Set(entries.map(entry => entry.toLowerCase()))
  const additions = parseEntries(draft).filter(entry => {
    const key = entry.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
  return [...entries, ...additions].join(',')
}

export const removeEntry = (value, index) =>
  parseEntries(value)
    .filter((_, i) => i !== index)
    .join(',')

// Edits a comma-separated string as a list of removable entries. The value
// stays a plain comma-joined string so backend consumers (which split on ',')
// are unaffected. Pasting a comma-separated list adds every entry at once.
export const CommaListEditor = ({ value, onChange, placeholder, addLabel }) => {
  const [draft, setDraft] = useState('')
  const entries = parseEntries(value)

  const handleAdd = () => {
    onChange(addEntries(value, draft))
    setDraft('')
  }

  return (
    <div>
      <div className="mb-2">
        {entries.length === 0 && (
          <span className="text-muted" style={{ fontSize: '0.9em' }}>
            No entries yet.
          </span>
        )}
        {entries.map((entry, index) => (
          <span
            key={`${entry}-${index}`}
            className="badge badge-secondary mr-1 mb-1"
            style={{ fontSize: '0.85em', fontWeight: 'normal' }}
          >
            {entry}
            <button
              type="button"
              onClick={() => onChange(removeEntry(value, index))}
              aria-label={`Remove ${entry}`}
              title={`Remove ${entry}`}
              style={{
                background: 'none',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                padding: '0 0 0 6px',
              }}
            >
              &times;
            </button>
          </span>
        ))}
      </div>
      <div className="input-group input-group-sm">
        <Form.Control
          type="text"
          placeholder={placeholder}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleAdd()
            }
          }}
        />
        <div className="input-group-append">
          <button
            type="button"
            className="btn btn-myed btn-default btn-default-myed"
            onClick={handleAdd}
            disabled={!draft.trim()}
          >
            <div className="fa-regular fa-plus fa-fw mr-1" />
            {addLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

CommaListEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  addLabel: PropTypes.string,
}

CommaListEditor.defaultProps = {
  value: '',
  placeholder: '',
  addLabel: 'Add',
}
